import { useEffect, useMemo, useRef, useState } from "react";
import { FileText } from "lucide-react";
import { toast } from "sonner";
import api, { getErrorMessage, getBlobErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DATE_PRESETS, presetRange } from "@/components/ui/date-presets";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";

const PRESET_LABELS = {
  "1d": "Last day",
  "1w": "Last week",
  "1m": "Last month",
  "6m": "Last 6 months",
  "1y": "Last year",
  "5y": "Last 5 years",
};

const DATE_RANGE_OPTIONS = [
  { value: "all", label: "All Time" },
  ...DATE_PRESETS.map((p) => ({ value: p.key, label: PRESET_LABELS[p.key] })),
  { value: "custom", label: "Custom Range" },
];

const isPresetKey = (v) => DATE_PRESETS.some((p) => p.key === v);

// Real JS port of the backend's canonical ledger_poc/schema.py _norm(): NFKC +
// casefold(lowercase) + collapse runs of non-alphanumeric chars to a single
// space + trim. Keeps client-side identity matching (which raw investor names
// collapse together in the picker) in lockstep with the server's real match.
const normKey = (s) => (s || "").normalize("NFKC").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim();
const namesMatch = (a, b) => normKey(a) === normKey(b);

export default function GenerateReportDialog({
  // Legacy plain-string fund list — the demo (onGenerate) path only.
  funds = [],
  // Flat member rows: {portal, fund_name, fund_display_name, investor_name}.
  // Drives the Client select + client-scoped fund checkboxes for the live
  // (api.post) path. Ignored when `onGenerate` is supplied.
  positions = [],
  // Optional client groups: [{group_id, name, members: [rawInvestorName, ...]}].
  // Rendered as a "Groups" optgroup listed first in the Client select (live
  // path only). Selecting one submits {group_id} instead of {investor_name}.
  groups = [],
  // When provided, the report HTML is built client-side from this callback
  // instead of POSTing to /ledger/html-report. Used by the demo dashboard.
  onGenerate = null,
}) {
  const isLivePath = onGenerate == null;

  const [open, setOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [xlsxGenerating, setXlsxGenerating] = useState(false);
  const [phase, setPhase] = useState("config"); // "config" | "preview"
  const [reportHtml, setReportHtml] = useState("");
  const iframeRef = useRef(null);

  const today = new Date().toISOString().slice(0, 10);

  const [title, setTitle] = useState(`Portfolio Report — ${today}`);
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedFunds, setSelectedFunds] = useState([]);
  const [showOverview, setShowOverview] = useState(true);
  const [showCashflow, setShowCashflow] = useState(true);
  const [dateRange, setDateRange] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  // v4 tenants: {groups, members} from /ledger/report-options. Any entry switches the dialog to the
  // Groups / Members multi-selects (empty selection = everyone); otherwise today's Client flow.
  const [reportOptions, setReportOptions] = useState({ groups: [], members: [] });
  const [selectedGroupNames, setSelectedGroupNames] = useState([]);
  const [selectedMemberNames, setSelectedMemberNames] = useState([]);
  const isV4Report = isLivePath && (reportOptions.groups.length > 0 || reportOptions.members.length > 0);

  useEffect(() => {
    if (!open || !isLivePath) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/ledger/report-options");
        const d = res?.data || {};
        if (!cancelled) {
          setReportOptions({
            groups: Array.isArray(d.groups) ? d.groups.filter((n) => typeof n === "string" && n) : [],
            members: Array.isArray(d.members) ? d.members.filter((n) => typeof n === "string" && n) : [],
          });
        }
      } catch {
        if (!cancelled) setReportOptions({ groups: [], members: [] });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, isLivePath]);

  const toggleIn = (setter) => (name) =>
    setter((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));
  const toggleGroupName = toggleIn(setSelectedGroupNames);
  const toggleMemberName = toggleIn(setSelectedMemberNames);

  // Client select value for a group is prefixed so it can't collide with a raw
  // investor_name string (group_ids are opaque; names are free text).
  const GROUP_VALUE_PREFIX = "g:";
  // Gate on actual group membership, never on the "g:" prefix alone — a raw
  // investor literally named "g:..." must never misroute to the group path.
  const isGroupSelection = groups.some((g) => selectedClient === GROUP_VALUE_PREFIX + g.group_id);
  const selectedGroupId = isGroupSelection ? selectedClient.slice(GROUP_VALUE_PREFIX.length) : null;
  const selectedGroup = isGroupSelection ? groups.find((g) => g.group_id === selectedGroupId) || null : null;

  // Raw investor names already covered by a group — excluded from the
  // "Ungrouped clients" list so they only show as their group entry.
  const groupCoveredNames = useMemo(() => {
    if (!isLivePath) return [];
    return groups.flatMap((g) => g.members || []);
  }, [groups, isLivePath]);

  // Distinct, sorted investor names from `positions` NOT covered by any group
  // (live path only).
  const investorOptions = useMemo(() => {
    if (!isLivePath) return [];
    const names = new Set(
      positions
        .map((p) => p.investor_name)
        .filter(Boolean)
        .filter((n) => !groupCoveredNames.some((m) => namesMatch(m, n)))
    );
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [positions, isLivePath, groupCoveredNames]);

  // Funds scoped to the selected client (live path), or the legacy plain
  // string list normalized into {fund_name, fund_display_name} (demo path).
  // For a group selection, funds are the union across all matched investors.
  const fundOptions = useMemo(() => {
    if (!isLivePath) return funds.map((f) => ({ fund_name: f, fund_display_name: f }));
    if (!selectedClient) return [];
    const seen = new Map();
    const matchesClient = (investorName) =>
      isGroupSelection
        ? (selectedGroup?.members || []).some((m) => namesMatch(m, investorName))
        : investorName === selectedClient;
    for (const p of positions) {
      if (!matchesClient(p.investor_name)) continue;
      if (!seen.has(p.fund_name)) {
        seen.set(p.fund_name, p.fund_display_name || p.fund_name);
      }
    }
    return Array.from(seen, ([fund_name, fund_display_name]) => ({ fund_name, fund_display_name })).sort(
      (a, b) => a.fund_display_name.localeCompare(b.fund_display_name)
    );
  }, [isLivePath, funds, positions, selectedClient, isGroupSelection, selectedGroup]);

  const resetForm = () => {
    const d = new Date().toISOString().slice(0, 10);
    setTitle(`Portfolio Report — ${d}`);
    setSelectedClient("");
    setSelectedFunds([]);
    setSelectedGroupNames([]);
    setSelectedMemberNames([]);
    setShowOverview(true);
    setShowCashflow(true);
    setDateRange("all");
    setCustomFrom("");
    setCustomTo("");
    setPhase("config");
    setReportHtml("");
  };

  const handleClientChange = (v) => {
    setSelectedClient(v);
    setSelectedFunds([]);
  };

  const toggleFund = (fundName) => {
    setSelectedFunds((prev) =>
      prev.includes(fundName) ? prev.filter((f) => f !== fundName) : [...prev, fundName]
    );
  };

  const toggleAllFunds = () => {
    const allNames = fundOptions.map((f) => f.fund_name);
    if (selectedFunds.length === allNames.length) {
      setSelectedFunds([]);
    } else {
      setSelectedFunds(allNames);
    }
  };

  const buildConfig = () => ({
    title,
    // "Every visible fund checked" wire-encodes the same as "nothing
    // checked" — the server already treats null as "all funds" (see
    // backend/routes/ledger.py) and fund_names has a max_length=100 cap
    // that a large client's full fund list can exceed (DRB = 175 funds).
    fund_names:
      selectedFunds.length > 0 && selectedFunds.length < fundOptions.length ? selectedFunds : null,
    sections: {
      overview: showOverview,
      cashflow_table: showCashflow,
    },
    // Presets are resolved to a concrete custom range at generate time; the
    // backend only knows all/ytd/last_quarter/custom.
    date_range: isPresetKey(dateRange)
      ? { type: "custom", ...presetRange(dateRange) }
      : {
          type: dateRange,
          ...(dateRange === "custom" && {
            from: customFrom || null,
            to: customTo || null,
          }),
        },
  });

  // The identity part of the POST: v4 sends the group/member name filters, otherwise the
  // single client (group_id or investor_name) exactly as before.
  const identityBody = () => {
    const config = buildConfig();
    if (isV4Report) return { ...config, group_names: selectedGroupNames, member_names: selectedMemberNames };
    return isGroupSelection ? { ...config, group_id: selectedGroupId } : { ...config, investor_name: selectedClient };
  };

  // Runs the generation (api.post or onGenerate), fed by the current form
  // state. Used both by the initial "Generate Report" submit and by
  // "Regenerate" from the preview phase.
  const runGenerate = async () => {
    if (isLivePath && !isV4Report && !selectedClient) {
      toast.error("Select a client before generating a report.");
      return;
    }
    setGenerating(true);
    try {
      const config = buildConfig();
      let html;
      if (onGenerate) {
        // The demo report builder (frontend/src/lib/demoReport.js) is a
        // deliberately richer sales artifact and still reads
        // sections.chart / sections.metrics directly. The live dialog no
        // longer exposes those controls, so keep them hardcoded on for the
        // demo path ONLY so its output stays byte-stable.
        const demoConfig = {
          ...config,
          sections: {
            ...config.sections,
            chart: true,
            metrics: { tvpi: true, dpi: true, rvpi: true, irr: true },
          },
        };
        html = await onGenerate(demoConfig);
      } else {
        const body = identityBody();
        // No responseType:"blob" here — the route returns text/html, which
        // axios already yields as a plain string, and requesting it as a
        // Blob made every server error's .detail unreadable (getErrorMessage
        // reads err.response.data.detail off a parsed object, never a Blob).
        const res = await api.post("/ledger/html-report", body);
        html = typeof res.data === "string" ? res.data : await res.data.text();
      }
      setReportHtml(html);
      setPhase("preview");
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to generate report"));
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await runGenerate();
  };

  const handleRegenerate = async () => {
    await runGenerate();
  };

  // XLSX export (live path only — the demo path has no server round-trip to
  // ask for a workbook). Re-posts the same report config with format:"xlsx"
  // and downloads the returned bytes directly; this request IS legitimately
  // binary, so its error path uses getBlobErrorMessage instead of
  // getErrorMessage (which can't read .detail off a Blob).
  const handleDownloadXlsx = async () => {
    setXlsxGenerating(true);
    try {
      const config = buildConfig();
      const body = { ...identityBody(), format: "xlsx" };
      const res = await api.post("/ledger/html-report", body, {
        responseType: "blob",
      });
      const blob = res.data;
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = (title || "Portfolio_Report").replace(/[^a-zA-Z0-9_-]/g, "_") + ".xlsx";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Excel report downloaded");
    } catch (err) {
      toast.error(await getBlobErrorMessage(err, "Failed to generate report"));
    } finally {
      setXlsxGenerating(false);
    }
  };

  const handleIframeLoad = () => {
    const doc = iframeRef.current?.contentDocument;
    if (doc && doc.body) {
      doc.body.contentEditable = "true";
    }
  };

  const downloadHtml = (html) => {
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = (title || "Portfolio_Report").replace(/[^a-zA-Z0-9_-]/g, "_") + ".html";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    setTimeout(() => URL.revokeObjectURL(url), 5000);
    toast.success("Report downloaded");
    setOpen(false);
  };

  const handleDownload = () => {
    const doc = iframeRef.current?.contentDocument;
    if (doc && doc.body && doc.body.innerHTML.trim()) {
      const clone = doc.documentElement.cloneNode(true);
      clone.querySelectorAll("[contenteditable]").forEach((el) => el.removeAttribute("contenteditable"));

      const pristineDoc = new DOMParser().parseFromString(reportHtml, "text/html");
      const pristineText = (pristineDoc.body?.textContent || "").trim();
      const currentText = (doc.body.textContent || "").trim();

      if (currentText !== pristineText) {
        const cloneBody = clone.querySelector("body");
        if (cloneBody) {
          const footer = doc.createElement("div");
          footer.textContent = "This report was manually edited after generation.";
          cloneBody.appendChild(footer);
        }
      }
      downloadHtml("<!doctype html>\n" + clone.outerHTML);
    } else {
      downloadHtml(reportHtml);
    }
  };

  const checkClass = "mr-2 accent-[#C41E2A]";

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) resetForm();
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="gap-1.5">
          <FileText className="w-4 h-4" />
          Generate Report
        </Button>
      </DialogTrigger>
      <DialogContent
        className={
          phase === "preview"
            ? "sm:max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            : "sm:max-w-lg max-h-[85vh] overflow-y-auto"
        }
      >
        <DialogHeader>
          <DialogTitle>Generate HTML Report</DialogTitle>
        </DialogHeader>

        {phase === "config" ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Title */}
            <div className="space-y-1.5">
              <Label htmlFor="gr-title">Report Title</Label>
              <Input
                id="gr-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Portfolio Report"
                maxLength={200}
              />
            </div>

            {/* v4: Groups / Members multi-selects (empty = everyone) */}
            {isV4Report && (
              <>
                {[
                  { label: "Groups", options: reportOptions.groups, selected: selectedGroupNames, toggle: toggleGroupName },
                  { label: "Members", options: reportOptions.members, selected: selectedMemberNames, toggle: toggleMemberName },
                ].map(({ label, options, selected, toggle }) =>
                  options.length > 0 && (
                    <div key={label} className="space-y-1.5" role="group" aria-label={label}>
                      <Label>{label}</Label>
                      <div className="max-h-32 overflow-y-auto border rounded-md p-2 space-y-1">
                        {options.map((name) => (
                          <label
                            key={name}
                            className="flex items-center text-sm cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5"
                          >
                            <input
                              type="checkbox"
                              checked={selected.includes(name)}
                              onChange={() => toggle(name)}
                              className={checkClass}
                            />
                            {name}
                          </label>
                        ))}
                      </div>
                      <p className="text-xs text-gray-400">
                        {selected.length === 0 ? `All ${label.toLowerCase()} included` : `${selected.length} selected`}
                      </p>
                    </div>
                  )
                )}
              </>
            )}

            {/* Client (live path only; v4 uses Groups / Members above) */}
            {isLivePath && !isV4Report && (
              <div className="space-y-1.5">
                <Label htmlFor="gr-client">Client</Label>
                <Select value={selectedClient} onValueChange={handleClientChange}>
                  <SelectTrigger id="gr-client" aria-required="true">
                    <SelectValue placeholder="Select a client" />
                  </SelectTrigger>
                  <SelectContent>
                    {groups.length > 0 && (
                      <SelectGroup>
                        <SelectLabel>Groups</SelectLabel>
                        {groups.map((g) => (
                          <SelectItem key={g.group_id} value={`${GROUP_VALUE_PREFIX}${g.group_id}`}>
                            {g.name}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                    <SelectGroup>
                      <SelectLabel>Ungrouped clients</SelectLabel>
                      {investorOptions.map((name) => (
                        <SelectItem key={name} value={name}>
                          {name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Fund Selection */}
            {fundOptions.length > 0 && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label>Funds</Label>
                  <button
                    type="button"
                    onClick={toggleAllFunds}
                    className="text-xs text-[#C41E2A] hover:underline"
                  >
                    {selectedFunds.length === fundOptions.length ? "Clear All" : "Select All"}
                  </button>
                </div>
                <div className="max-h-32 overflow-y-auto border rounded-md p-2 space-y-1">
                  {fundOptions.map((f) => (
                    <label
                      key={f.fund_name}
                      className="flex items-center text-sm cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5"
                    >
                      <input
                        type="checkbox"
                        checked={selectedFunds.includes(f.fund_name)}
                        onChange={() => toggleFund(f.fund_name)}
                        className={checkClass}
                      />
                      {f.fund_display_name}
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-400">
                  {selectedFunds.length === 0 ? "All funds included" : `${selectedFunds.length} fund(s) selected`}
                </p>
              </div>
            )}

            {/* Sections */}
            <div className="space-y-1.5">
              <Label>Sections</Label>
              <div className="space-y-1">
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    checked={showOverview}
                    onChange={(e) => setShowOverview(e.target.checked)}
                    className={checkClass}
                  />
                  Overview Table
                </label>
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    checked={showCashflow}
                    onChange={(e) => setShowCashflow(e.target.checked)}
                    className={checkClass}
                  />
                  Recent activity
                </label>
              </div>
            </div>

            {/* Date Range */}
            <div className="space-y-1.5">
              <Label htmlFor="gr-daterange">Date Range</Label>
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger id="gr-daterange">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DATE_RANGE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Custom date inputs */}
            {dateRange === "custom" && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="gr-from">From</Label>
                  <Input
                    id="gr-from"
                    type="date"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="gr-to">To</Label>
                  <Input id="gr-to" type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={generating}>
                {generating ? "Generating..." : "Generate Report"}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-gray-500">
              Click any text in the preview to edit it before downloading.
            </p>
            <iframe
              ref={iframeRef}
              title="Report preview"
              srcDoc={reportHtml}
              onLoad={handleIframeLoad}
              sandbox="allow-same-origin"
              className="w-full h-[70vh] border rounded-md bg-white"
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPhase("config")}>
                Back
              </Button>
              <Button type="button" variant="outline" onClick={handleRegenerate} disabled={generating}>
                {generating ? "Regenerating..." : "Regenerate"}
              </Button>
              {isLivePath && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleDownloadXlsx}
                  disabled={xlsxGenerating}
                >
                  {xlsxGenerating ? "Preparing..." : "Download Excel"}
                </Button>
              )}
              <Button type="button" onClick={handleDownload}>
                Download
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
